import {createContext, type PropsWithChildren, useCallback, use, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'
import {filterRegexForQueryString} from '../utilities/filters'

interface SortContextType {
  resetSort: () => void
  sort: string
  setSort: (sort: string) => void
  isDefaultSort: boolean
}

// Keep in sync with MarketplaceHelper::DEFAULT_SORT_VALUE in Ruby.
const defaultSortValue = 'popularity-desc'

const SortContext = createContext<SortContextType | undefined>(undefined)

export function useSort() {
  const context = use(SortContext)
  if (!context) throw new Error('useSort must be used within a SortProvider')
  return context
}

export function SortProvider({children}: PropsWithChildren) {
  const [searchParams] = useSearchParams()
  const [sort, setSort] = useState(() => {
    const queryFromParams = searchParams.get('query')
    let sortValue: string | undefined
    if (queryFromParams) {
      const match = queryFromParams.match(filterRegexForQueryString('sort'))
      sortValue = match ? match[1] : undefined
    }

    switch (sortValue) {
      case 'created-desc':
        return 'created-desc'
      case 'match-desc':
        return 'match-desc'
      default:
        return defaultSortValue
    }
  })

  const resetSort = useCallback(() => setSort(defaultSortValue), [])

  const value = useMemo(
    () => ({sort, isDefaultSort: sort === defaultSortValue, resetSort, setSort}),
    [sort, resetSort, setSort],
  )
  return <SortContext value={value}>{children}</SortContext>
}
